import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  Form,
  FormProvider,
  ValidatedRadioGroup,
  ValidatedTextArea,
} from '@tq/ui-components/dist/components/forms-engine';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  Grid,
  Radio,
  Text,
  useThemedValueSwitch,
  VisuallyHidden,
} from '@tq/ui-components';

import { useAddNote } from 'api';
import { consulteeIdState, showAddNoteState } from 'store';

type StyledAlertProps = {
  status: 'info' | 'warning';
  description: string;
};

const StyledAlert: React.FC<StyledAlertProps> = (props) => {
  const { status, description } = props;
  const { tvs, isNI } = useThemedValueSwitch();

  return (
    <Alert
      status={status}
      sx={{
        '@media screen and (min-width: 48em)': {
          p: tvs({ tq: '.65rem' }),
        },
        bg: tvs({ tq: 'white' }),
        ...(isNI ? { border: 'none' } : {}),
      }}
    >
      <AlertIcon w='1.25rem' h='1.25rem' mr='0.5rem' />
      <AlertDescription lineHeight='1.25rem' fontSize={tvs({ tq: '0.875rem' })}>
        {description}
      </AlertDescription>
    </Alert>
  );
};

export const AddNote: React.FC = () => {
  const noteTextMaxLength = 250;

  const { t } = useTranslation('notes');
  const { tvs } = useThemedValueSwitch();

  const consulteeId = useRecoilValue(consulteeIdState);
  const { id } = useParams();
  const { mutate: addNote } = useAddNote();

  const [showAddNote, setShowAddNote] = useRecoilState(showAddNoteState);
  const [noteTypeId, setNoteTypeId] = useState(0);
  const [noteText, setNoteText] = useState('');
  const [noteAddState, setNoteAddState] = useState(false);

  const handleCancel = () => {
    setShowAddNote(false);
    setNoteTypeId(0);
    setNoteText('');
    setNoteAddState(true);
    setTimeout(() => setNoteAddState(false), 5000);
  };

  const handleSubmit = () => {
    addNote(
      {
        consultationId: +(id ?? 0),
        consulteeId,
        noteTypeId: +noteTypeId,
        noteText,
      },
      { onSuccess: handleCancel }
    );
  };

  return (
    <>
      {noteAddState && <VisuallyHidden>{t('note_add_text', 'Note added')}</VisuallyHidden>}
      {showAddNote && (
        <FormProvider>
          <Form id='add-note-form' onValidSubmit={handleSubmit}>
            <Box
              sx={{
                '>div': {
                  mb: tvs({ tq: '1.25rem' }),
                },
              }}
            >
              <ValidatedRadioGroup
                id='noteTypeId'
                value={noteTypeId}
                onChange={(value: string) => setNoteTypeId(+value)}
                aria-label={t('note_type_label', 'Select who will see this note:')}
                label={t('note_type_label', 'Select who will see this note:')}
                validation={{
                  required: {
                    value: true,
                    message: t('note_type_required_error', 'Please select a note type'),
                  },
                }}
              >
                <Grid mt='0.25rem' mb='1.25rem' gap='1rem'>
                  <Radio
                    id='internal'
                    data-testid='noteRadio1'
                    value={t('internal_id', '1').toString() ?? '1'}
                  >
                    {t('internal_radioText', 'Internal')}
                  </Radio>
                  <Radio
                    data-testid='noteRadio2'
                    id='external'
                    value={t('external_id', '2').toString() ?? '2'}
                  >
                    {t('external_radioText', 'External')}
                  </Radio>
                </Grid>
                {/*
                 * Changed the AttentionMessage to an Alert (inside StyledAlert) because although it worked fine for NI
                 * For TQ, there were some items that could not be reached using AttentionMessage
                 * So using the Alert instead which allows you to directly access the Icon, Title and Description
                 * to style them. This was useful for the TQ theme
                 * Have tested NI to ensure it still looks the same.
                 */}
                {(noteTypeId === 1 || noteTypeId === 5) && (
                  <StyledAlert
                    status='info'
                    description={t(
                      'internal_infoText',
                      'This note will be viewed by your organisation only'
                    )}
                  />
                )}
                {(noteTypeId === 2 || noteTypeId === 6) && (
                  <StyledAlert
                    status='warning'
                    description={t(
                      'external_infoText',
                      'People outside your organisation can view this note'
                    )}
                  />
                )}
              </ValidatedRadioGroup>
            </Box>
            <Box
              sx={{
                '[role="group"] label': {
                  mb: tvs({ tq: 0 }),
                },

                '[role="group"] div:nth-of-type(2)': {
                  fontSize: tvs({ tq: '0.875rem' }),
                  mt: tvs({ tq: '0.4375rem' }),
                  mb: tvs({ tq: '0.75rem' }),
                },
              }}
              id='the-container-101'
            >
              <ValidatedTextArea
                data-testid='noteTextArea'
                id='noteText'
                placeholder={t('note_text_placeholder', 'Enter your note here')}
                label={t('note_text_label', 'Note text')}
                maxLength={noteTextMaxLength}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  setNoteText(event.target.value);
                }}
                validation={{
                  required: {
                    value: true,
                    message: t('note_text_required_error', 'Please enter note text'),
                  },
                }}
              ></ValidatedTextArea>
            </Box>
            <Text size='sm' mt={-6}>
              {noteText?.length} of {noteTextMaxLength} characters
            </Text>
            <Grid gridAutoFlow='column' gridGap={4} mt={tvs({ tq: '1.25rem' }, '1rem')}>
              <Button
                variant={tvs({ tq: 'secondary' }, 'tertiary')}
                size='sm'
                onClick={handleCancel}
              >
                {t('cancel_button_text', 'Cancel')}
              </Button>
              <Button variant='primary' type='submit' size='sm'>
                {t('submit_button_text', 'Submit')}
              </Button>
            </Grid>

            <Divider my={tvs({ tq: '1.25rem' }, 8)} />
          </Form>
        </FormProvider>
      )}
    </>
  );
};
